import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useParams } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LICENSED_PARTNERS_SPORT as BASE_PATH } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { MLBTeams } from '@sorare/core/src/lib/mlbTeams';
import { NBATeams } from '@sorare/core/src/lib/nbaTeams';
import { metadatas } from '@sorare/core/src/lib/seo/common';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { PartnersList } from './PartnersList/index.tsx';
import type {
  LicensedClubsQuery,
  LicensedClubsQueryVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  width: 100%;
  justify-content: flex-start;
`;

const Header = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: flex-start;
`;

const Count = styled.span`
  --count-font-size: clamp(10rem, calc(30 * var(--vw)), 20rem);
  font-family: var(--brand-font);
  font-size: var(--count-font-size);
  margin-bottom: calc(-0.35 * var(--count-font-size) + var(--double-unit));

  @media ${tabletAndAbove} {
    margin-bottom: calc(-0.35 * var(--count-font-size) + var(--quadruple-unit));
  }
`;

const Licensed = styled.span`
  font-size: clamp(2rem, calc(10 * var(--vw)), 5rem);
  color: var(--c-blue-300);
`;

const Teams = styled.span`
  font-size: clamp(2rem, calc(10 * var(--vw)), 5rem);
`;

const LicensedTeams = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${tabletAndAbove} {
    align-items: baseline;
    flex-direction: row;
    gap: var(--double-unit);
  }
  & > *:first-child {
    margin-bottom: calc(-2 * var(--unit));
  }
`;

const Subtitle = styled.div`
  color: var(--c-nd-600);
  text-align: center;
  padding: var(--quadruple-unit) 0;
  width: 80%;
  font: var(--t-body-m);
  @media ${tabletAndAbove} {
    font: var(--t-body-l);
    width: 70%;
  }
  @media ${desktopAndAbove} {
    width: 50%;
  }
`;

const TabsWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  padding-bottom: var(--quadruple-unit);
`;

const LICENSED_CLUBS_QUERY = gql`
  query LicensedClubsQuery {
    football {
      clubsReady {
        slug
        ...PartnersList_club
      }
    }
  }
  ${PartnersList.fragments.club}
` as TypedDocumentNode<LicensedClubsQuery, LicensedClubsQueryVariables>;

export const LicensedPartners = () => {
  useTitleAndDescription(
    metadatas.licensedPartners.title,
    metadatas.licensedPartners.description
  );

  const { data, loading } = useQuery(LICENSED_CLUBS_QUERY);
  const [displayedCount, setDisplayedCount] = useState(0);
  const { sport, tab } = useParams();

  useEffect(() => {
    setTimeout(() => {
      setDisplayedCount(
        (data?.football.clubsReady.length || 0) +
          MLBTeams.length +
          NBATeams.length
      );
    }, 0);
  }, [data?.football.clubsReady.length]);
  return (
    <>
      <Header>
        <Wrapper>
          <FormattedMessage
            id="LicensedPartners.title"
            defaultMessage="<a>{count}</a><d><b>licensed</b><c>teams</c></d>"
            values={{
              count: loading ? (
                '300+'
              ) : (
                <AnimatedNumber value={displayedCount} />
              ),
              a: text => <Count>{text}</Count>,
              b: text => <Licensed>{text}</Licensed>,
              c: text => <Teams>{text}</Teams>,
              d: text => <LicensedTeams>{text}</LicensedTeams>,
            }}
          />
        </Wrapper>
        <Subtitle>
          <FormattedMessage
            id="LicensedPartners.subtitle"
            defaultMessage="We have partnered with the best sports leagues and clubs in the world to bring our next-level fantasy sports game to fans across the globe"
          />
        </Subtitle>
      </Header>
      <TabsWrapper>
        <TabBar variant="secondary">
          <Tab
            to={generatePath(BASE_PATH, { sport: 'football' })}
            matches={[catchAll(generatePath(BASE_PATH, { sport: 'football' }))]}
          >
            Football
          </Tab>
          <Tab to={generatePath(BASE_PATH, { sport: 'nba' })}>NBA</Tab>
          <Tab to={generatePath(BASE_PATH, { sport: 'mlb' })}>MLB</Tab>
        </TabBar>
      </TabsWrapper>
      <PartnersList
        footballClubs={data?.football.clubsReady}
        sport={sport}
        tab={tab}
        loading={loading}
      />
    </>
  );
};
